import { Component, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FleetTrackingService, VehicleDailyStatus, DayMovement, StatusCatalog, UserPermissions } from '../../../core/services/fleet-tracking.service'
import {GeneralService} from "../../../core/services/general.service";

@Component({
  selector: 'app-movements',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './movements.component.html',
  styleUrls: ['./movements.component.scss']
})
export class MovementsComponent implements OnInit, AfterViewInit {
  // Datos del tablero
  vehicles: VehicleDailyStatus[] = [];
  statusCatalog: StatusCatalog[] = [];

  // Permisos del usuario
  userPermissions: UserPermissions = {
    canCreateMovements: false,
    canEditHistory: false
  };
  permissionsLoaded: boolean = false;

  // Control de fecha
  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth() + 1;
  daysInMonth: number = 30;
  daysArray: number[] = [];
  monthNames: string[] = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Modal de detalles
  showDetailsModal: boolean = false;
  selectedDayMovements: DayMovement[] = [];
  selectedVehicle: VehicleDailyStatus | null = null;
  selectedDay: number = 1;

  // Modal de entrada/salida
  showEntryExitModal: boolean = false;
  movementForm = {
    vehicleId: 0,
    statusId: 0,
    serviceId: null as number | null,
    startDate: '',
    comments: ''
  };

  // Loading
  isLoading: boolean = false;

  today: Date = new Date();
  currentDay: number = new Date().getDate();

  constructor(private fleetService: FleetTrackingService, private el: ElementRef, private generalService: GeneralService) {}

  ngOnInit(): void {
    this.loadUserPermissions();
    this.loadStatusCatalog();
    this.loadMonthlyBoard();
  }

  ngAfterViewInit(): void {
    //this.syncScrolls();
  }

  /**
   * Cargar permisos del usuario
   */
  loadUserPermissions(): void {
    this.fleetService.getUserPermissions().subscribe({
      next: (response) => {
        if (!response.error) {
          this.userPermissions = response.data;
          this.permissionsLoaded = true;
        } else {
          // Si hay error, dejar permisos en false por seguridad
          this.permissionsLoaded = true;
        }
      },
      error: (error) => {
        console.error('Error al cargar permisos:', error);
        this.permissionsLoaded = true;
      }
    });
  }

  /**
   * Sincronizar scroll del header con el body (bidireccional)
   */
  private syncScrolls(): void {
    const headerWrapper = this.el.nativeElement.querySelector('.days-header-wrapper');
    const bodyWrapper = this.el.nativeElement.querySelector('.grid-body-wrapper');

    if (!headerWrapper || !bodyWrapper) {
      console.warn('⚠️ No se pudieron sincronizar los scrolls');
      return;
    }

    let isScrollingFromHeader = false;
    let isScrollingFromBody = false;

    // Cuando el BODY hace scroll → sincronizar HEADER
    bodyWrapper.addEventListener('scroll', () => {
      if (isScrollingFromHeader) {
        isScrollingFromHeader = false;
        return;
      }
      isScrollingFromBody = true;
      headerWrapper.scrollLeft = bodyWrapper.scrollLeft;
    });

    // Cuando el HEADER hace scroll → sincronizar BODY
    headerWrapper.addEventListener('scroll', () => {
      if (isScrollingFromBody) {
        isScrollingFromBody = false;
        return;
      }
      isScrollingFromHeader = true;
      bodyWrapper.scrollLeft = headerWrapper.scrollLeft;
    });

    console.log('✅ Scrolls sincronizados correctamente');
  }

  /**
   * Cargar catálogo de estatus
   */
  loadStatusCatalog(): void {
    this.fleetService.getStatusCatalog().subscribe({
      next: (response: { error: any; data: StatusCatalog[]; }) => {
        if (!response.error) {
          this.statusCatalog = response.data;
        }
      },
      error: (error) => {
        console.error('Error al cargar catálogo:', error);
      }
    });
  }

  /**
   * Cargar tablero mensual
   */
  loadMonthlyBoard(): void {
    this.isLoading = true;
    this.fleetService.getMonthlyBoard(this.currentYear, this.currentMonth).subscribe({
      next: (response: { error: any; data: { vehicles: VehicleDailyStatus[]; daysInMonth: number; }; }) => {
        if (!response.error) {
          this.vehicles = response.data.vehicles;
          this.daysInMonth = response.data.daysInMonth;
          this.daysArray = Array.from({ length: this.daysInMonth }, (_, i) => i + 1);
        }
        this.isLoading = false;
        setTimeout(() => {
          this.syncScrolls();
        }, 200);
      },
      error: (error) => {
        console.error('Error al cargar tablero:', error);
        this.isLoading = false;
      }
    });
  }

  /**
   * Cambiar mes
   */
  changeMonth(direction: number): void {
    this.currentMonth += direction;

    if (this.currentMonth > 12) {
      this.currentMonth = 1;
      this.currentYear++;
    } else if (this.currentMonth < 1) {
      this.currentMonth = 12;
      this.currentYear--;
    }

    this.loadMonthlyBoard();
  }

  /**
   * Obtener el movimiento principal del día (el de mayor duración)
   */
  getPrimaryMovement(movements: DayMovement[]): DayMovement | null {
    if (!movements || movements.length === 0) return null;
    return movements[movements.length - 1]; // Toma el último
  }

  /**
   * Verificar si un día tiene múltiples movimientos
   */
  hasMultipleMovements(movements: DayMovement[]): boolean {
    return movements && movements.length > 1;
  }

  /**
   * Abrir modal de detalles del día
   */
  openDayDetails(vehicle: VehicleDailyStatus, day: number): void {
    const movements = vehicle.dailyStatus[day];

    if (!movements || movements.length === 0) {
      return;
    }

    this.selectedVehicle = vehicle;
    this.selectedDay = day;
    this.selectedDayMovements = movements;
    this.showDetailsModal = true;
  }

  /**
   * Cerrar modal de detalles
   */
  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedDayMovements = [];
    this.selectedVehicle = null;
  }

  /**
   * Abrir modal de entrada/salida
   */
  openEntryExitModal(vehicleId?: number): void {
    // Validar permisos antes de abrir modal
    if (!this.userPermissions.canCreateMovements) {
      this.generalService.showMessage('warn', 'No tienes permisos para registrar movimientos. Contacta al administrador.');
      return;
    }

    this.movementForm = {
      vehicleId: vehicleId || 0,
      statusId: 0,
      serviceId: null,
      startDate: new Date().toISOString().slice(0, 16),
      comments: ''
    };
    this.showEntryExitModal = true;
  }

  /**
   * Cerrar modal de entrada/salida
   */
  closeEntryExitModal(): void {
    this.showEntryExitModal = false;
  }

  /**
   * Guardar nuevo movimiento
   */
  saveMovement(): void {
    if (!this.movementForm.vehicleId || !this.movementForm.statusId || !this.movementForm.startDate) {
      this.generalService.showMessage('warn', 'Por favor completa todos los campos requeridos');
      return;
    }

    // Convertir formato de fecha para el backend
    const startDate = this.movementForm.startDate.replace('T', ' ') + ':00';

    this.fleetService.createMovement({
      ...this.movementForm,
      startDate
    }).subscribe({
      next: (response) => {
        if (!response.error) {
          this.generalService.showMessage('success', 'Movimiento registrado exitosamente');
          this.closeEntryExitModal();
          this.loadMonthlyBoard();
        } else {
          this.generalService.showMessage('warn', 'Error: ' + response.message);
        }
      },
      error: (error) => {
        console.error('Error al guardar movimiento:', error);
        this.generalService.showMessage('warn', 'Error al guardar el movimiento');
      }
    });
  }

  /**
   * Obtener todas las subcategorías de un estatus padre
   */
  getSubcategories(parentId: number): StatusCatalog[] {
    const parent = this.statusCatalog.find(s => s.id === parentId);
    return parent?.subcategories || [];
  }

  /**
   * Formatear hora
   */
  formatTime(time: string): string {
    return time.substring(0, 5); // HH:mm
  }

  /**
   * Obtener nombre del mes actual
   */
  get currentMonthName(): string {
    return this.monthNames[this.currentMonth - 1];
  }

  /**
   * Calcular ancho de columna basado en días del mes
   */
  get dayColumnWidth(): string {
    // Mínimo 40px, máximo ajustado al contenedor
    const minWidth = 40;
    return `${minWidth}px`;
  }

  /**
   * Verificar si un día es futuro
   */
  isFutureDay(day: number): boolean {
    const checkDate = new Date(this.currentYear, this.currentMonth - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return checkDate > today;
  }

  /**
   * Verificar si un día es hoy
   */
  isToday(day: number): boolean {
    const today = new Date();
    return day === today.getDate() &&
        this.currentMonth === (today.getMonth() + 1) &&
        this.currentYear === today.getFullYear();
  }

  /**
   * Verificar si debe mostrar el movimiento en este día
   */
  shouldShowMovement(day: number): boolean {
    return !this.isFutureDay(day);
  }
}
